import type { FilterConfig, LayerFilterDef, NumericColorRange } from './filter.js';

/** @deprecated Решение «тайлить» теперь по ВЕСУ слоя (API env `PUBLIC_VECTOR_TILE_WEIGHT_THRESHOLD_BYTES`). */
export const PUBLIC_VECTOR_TILE_AUTO_THRESHOLD = 2000;

/**
 * Зум переключения тайлового preview-point: ниже — кружки (cover URL в тайл не кладётся),
 * с этого зума и выше — символы с превью-иконками (cover URL печётся в тайл). Общий контракт
 * сервера (зум-гейт cover-атрибута в MVT) и клиента (minzoom символов / maxzoom кружков).
 */
export const PUBLIC_VECTOR_TILE_PREVIEW_SWITCH_ZOOM = 14;

export type LayerType = 'vector' | 'raster' | 'image' | 'heatmap' | 'model-3d';

/** MapLibre raster paint keys used for georeferenced image instances (not tile raster layers). */
export const IMAGE_INSTANCE_RASTER_PAINT_KEYS = [
  'raster-opacity',
  'raster-brightness-min',
  'raster-brightness-max',
  'raster-saturation',
  'raster-contrast',
  'raster-hue-rotate',
] as const;

export type ImageInstanceRasterPaintKey = (typeof IMAGE_INSTANCE_RASTER_PAINT_KEYS)[number];

export type ImageInstanceRasterPaint = Partial<Record<ImageInstanceRasterPaintKey, number>>;

export const IMAGE_INSTANCE_RASTER_PAINT_DEFAULTS: Record<ImageInstanceRasterPaintKey, number> = {
  'raster-opacity': 1,
  'raster-brightness-min': 0,
  'raster-brightness-max': 1,
  'raster-saturation': 0,
  'raster-contrast': 0,
  'raster-hue-rotate': 0,
};

/** One georeferenced image in an image layer (several per layer) */
export interface ImageOverlayInstance {
  id: string;
  imageUrl: string;
  /** Four corner coordinates [[lng,lat] × 4] */
  coordinates: [number, number][];
  /** Подпись в списке слоя */
  label?: string;
  /** Кастомные параметры (как у векторных объектов) */
  properties?: Record<string, unknown>;
  /** MapLibre raster paint overrides for this instance (прозрачность, яркость, …) */
  rasterPaint?: ImageInstanceRasterPaint;
  /** XYZ tiles URL (viewer optimization), optional */
  tilesUrl?: string;
  tileBounds?: [number, number, number, number];
  tileMinZoom?: number;
  tileMaxZoom?: number;
}

/** Instance of a 3D model at a specific position (for model-3d layer) */
export interface Model3DInstance {
  id: string;
  lngLat: [number, number];
  altitude?: number;
  scale?: number;
  rotation?: [number, number, number];
  /** Подпись в списке слоя и на карте (аналог подписи у векторных объектов) */
  label?: string;
  /** URL gltf/glb; если не задан — используется modelUrl слоя */
  modelUrl?: string;
  /** Значения кастомных параметров слоя (как properties у GeoJSON) */
  properties?: Record<string, unknown>;
}

/** Custom property definition for user-created layer schemas */
export type CustomPropertyType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'select'
  | 'title'
  | 'cover'
  | 'markdown'
  | 'link'
  | 'file'
  | 'angle'
  | 'date';

export interface CustomPropertyDef {
  name: string;
  /** Человекочитаемое имя в UI (раскраска, фильтры). По умолчанию — `name`. */
  label?: string;
  type: CustomPropertyType;
  /** For type 'array' or 'select': allowed values for selection */
  options?: string[];
  /** For type 'array' or 'select': display label per stored option value */
  optionLabels?: Record<string, string>;
  /** Системный параметр: участвует в карточке/визуализации, но не редактируется generic-формой объекта. */
  hiddenInEditor?: boolean;
}

/** Card title config: either from an existing param or free text (stored in param of type 'title') */
export interface CardTitleConfig {
  mode: 'from_param' | 'free_text';
  /** Property name: for from_param — existing param; for free_text — param of type 'title' */
  paramName: string;
}

export interface LayerLegendConfig {
  /** Show object legend in the public viewer */
  enabled: boolean;
  /** Show cover previews near object names (requires a cover-type property) */
  showPreview: boolean;
  /** Show this layer on the public map (default: true) */
  showOnPublicMap?: boolean;
  /** Show this layer in the viewer side panel (filter/legend/toggle). When false the layer is still rendered on the map. (default: true) */
  showInPublicLayerPanel?: boolean;
  /** Show toggle switch for this layer in the public viewer panel (default: true) */
  allowToggle?: boolean;
  /** Default visibility state on the public map when allowToggle is true (default: true) */
  defaultVisible?: boolean;
  /** Icon URL for the layer panel in the public viewer */
  iconUrl?: string;
  /** Layer description shown in the public viewer panel (supports markdown) */
  description?: string;
  /** Allow clicking on features of this layer on the public map (default: true) */
  publicFeaturesClickable?: boolean;
}

export interface LayerSourceConfig {
  /** Martin table name for vector layers */
  martinTable?: string;
  /** Image URL for image overlay layers (legacy single image; prefer imageInstances) */
  imageUrl?: string;
  /** Несколько геопривязанных изображений в одном слое */
  imageInstances?: ImageOverlayInstance[];
  /** XYZ tiles URL for a single legacy image overlay (copied onto instance when normalizing) */
  tilesUrl?: string;
  tileBounds?: [number, number, number, number];
  tileMinZoom?: number;
  tileMaxZoom?: number;
  /** Tile URL template for raster layers */
  tiles?: string[];
  /** Tile size for raster layers */
  tileSize?: number;
  /** Bounding coordinates for image layers [[lng,lat] x4] */
  coordinates?: [number, number][];
  /** Bounding box [west, south, east, north] */
  bounds?: [number, number, number, number];
  /** Custom property schema for features in this layer */
  customProperties?: CustomPropertyDef[];
  /** Card title config for object cards (editor + viewer) */
  cardTitleConfig?: CardTitleConfig;
  /** Bounding box для импорта OSM-зданий [west, south, east, north] */
  osmBbox?: [number, number, number, number];
  /** Параметрические фильтры для слоя (новая система) */
  filterDefs?: LayerFilterDef[];
  /**
   * Публичная карта: когда отдавать геометрию тайлами вместо GeoJSON в `GET /public`.
   * `auto` — по порогу `PUBLIC_VECTOR_TILE_AUTO_THRESHOLD`; `on` / `off` — принудительно.
   */
  publicVectorTileMode?: 'auto' | 'on' | 'off';
  /** Публичная карта: конкретный tiled backend (`mvt` по API или `pmtiles` архив). */
  publicTilesKind?: 'mvt' | 'pmtiles';
  /** Публичная карта: URL PMTiles-архива, если `publicTilesKind === 'pmtiles'`. */
  publicPmtilesUrl?: string;
  /** Публичная карта: версия PMTiles (generation). */
  publicPmtilesVersion?: string | number;
  /** Source layer внутри PMTiles-архива. */
  publicVectorSourceLayer?: string;
  /** Публичная карта: min зум пирамиды PMTiles (для MapLibre `minzoom` источника). */
  publicPmtilesMinZoom?: number;
  /** Публичная карта: max зум пирамиды PMTiles (для MapLibre `maxzoom` источника и overzoom). */
  publicPmtilesMaxZoom?: number;
  /** Настройки легенды слоя на публичной карте */
  legendConfig?: LayerLegendConfig;
  /** Дефолтный URL gltf/glb для экземпляров без собственного modelUrl */
  modelUrl?: string;
  /** Экземпляры 3D-модели с позициями и трансформациями */
  models?: Model3DInstance[];
}

/** Color palette ID for data-driven coloring */
export type ColorPaletteId =
  | 'category10'
  | 'pastel'
  | 'bold'
  | 'muted'
  | 'viridis'
  | 'plasma'
  | 'spectral';

/**
 * Способ визуализации одного виз-блока слоя. Геометрия выводится из kind
 * (см. `visualizationKindGeometry`), отдельно не хранится.
 */
export type VisualizationKind =
  | 'circle'
  | 'symbol'
  | 'preview-point'
  | 'heatmap'
  | 'line'
  | 'fill'
  | 'fill-extrusion'
  | 'outline';

/**
 * Виз-блок слоя: одна визуализация одной геометрии. Слой держит массив блоков
 * (`styleConfig.visualizations`) — так один слой рисует точки+линии+полигоны
 * и несколько представлений одной геометрии (точки + теплокарта).
 * Пер-виз настройки (colorBy/iconBy/preview/FOV/height) живут в блоке,
 * а не в корне styleConfig. См. docs/layers/plan.md.
 */
export interface LayerVisualization {
  id: string;
  kind: VisualizationKind;
  /** Выключенный блок не рендерится и не попадает в легенду; настройки сохраняются. */
  enabled: boolean;
  paint: Record<string, unknown>;
  layout: Record<string, unknown>;
  colorByProperty?: string;
  colorPalette?: ColorPaletteId;
  colorMapping?: Record<string, string>;
  colorFallback?: string;
  colorByNumericRanges?: NumericColorRange[];
  iconByProperty?: string;
  iconMapping?: Record<string, string>;
  previewParamName?: string;
  previewShape?: 'circle' | 'rounded-square' | 'pin' | 'free';
  previewSize?: number | unknown[];
  previewBorderWidth?: number;
  previewFreeBackground?: string;
  showFieldOfView?: boolean;
  fieldOfViewAngleProperty?: string;
  showSymbolRotation?: boolean;
  symbolRotationAngleProperty?: string;
  heightByProperty?: string;
  heightMultiplier?: number;
  heightFallback?: number;
  /** Для symbol: размер иконки в реальных px (см. SymbolIconSizeField). */
  iconSizePx?: number | unknown[];
  /** Для preview-point: якорь иконки превью. */
  previewAnchor?: string;
  /**
   * Для symbol: рисовать символ и на полигонах, а не только на точках.
   * MapLibre ставит его в «полюс недоступности» фигуры — визуальный центр.
   * По умолчанию выключено: у обычного слоя полигоны подписывает fill-блок
   * (саб-слой `-fl-lbl`), и включённый symbol дал бы вторую подпись поверх.
   */
  symbolOnPolygons?: boolean;
  /**
   * Symbol-блок не рисует подписи (text-field), даже при showLabels слоя.
   * Используется оверлеем иконок мест креатива: подписи рисует основной
   * circle-блок (-pt-lbl), иначе они дублировались бы.
   */
  suppressText?: boolean;
}

export interface LayerStyleConfig {
  /**
   * Legacy (см. `visualizations`): MapLibre layer type: circle, fill, line, symbol,
   * fill-extrusion, heatmap, raster. Читается только через `resolveVisualizations`.
   */
  type: string;
  /** MapLibre paint properties */
  paint: Record<string, unknown>;
  /** MapLibre layout properties */
  layout: Record<string, unknown>;
  /** Min zoom level */
  minzoom?: number;
  /** Max zoom level */
  maxzoom?: number;
  /** MapLibre filter expression */
  filter?: unknown[];
  /** Property name for point labels (used in text-field: ['get', labelProperty]) */
  labelProperty?: string;
  /** For circle layer: show labels on points (adds symbol sub-layer) */
  showLabels?: boolean;
  /**
   * When true, show field-of-view overlay (uses angle-type custom property).
   * For `type: 'preview-point'`, only honored when `previewShape` is `'circle'`.
   */
  showFieldOfView?: boolean;
  /** Name of custom property with `type: 'angle'` that drives FOV rotation; resolver falls back to first angle param */
  fieldOfViewAngleProperty?: string;
  /** When true, symbol icons rotate by an angle-type custom property */
  showSymbolRotation?: boolean;
  /** Name of custom property with `type: 'angle'` that drives symbol icon rotation */
  symbolRotationAngleProperty?: string;
  /** Property name for data-driven color (objects colored by this parameter's values) */
  colorByProperty?: string;
  /** Color palette ID when colorByProperty is set */
  colorPalette?: ColorPaletteId;
  /** For preview-point: which property holds the cover image URL */
  previewParamName?: string;
  /** For preview-point: shape of the photo container */
  previewShape?: 'circle' | 'rounded-square' | 'pin' | 'free';
  /** For preview-point: marker radius in px (default 24), or zoom interpolate/step like paint */
  previewSize?: number | unknown[];
  /** For preview-point: border/stroke width in px (default 3) */
  previewBorderWidth?: number;
  /** For preview-point shape 'free': optional background color (undefined = no background) */
  previewFreeBackground?: string;
  /** Кастомные числовые диапазоны для раскраски (расширение colorByProperty для чисел) */
  colorByNumericRanges?: NumericColorRange[];
  /** Явные цвета по значению параметра (для viewer / Martin при colorByProperty) */
  colorMapping?: Record<string, string>;
  /** Цвет объектов без значения параметра раскраски (MapLibre match fallback) */
  colorFallback?: string;
  /** Параметр для data-driven `layout['icon-image']` (тип symbol) */
  iconByProperty?: string;
  /** Иконка по значению параметра: значение параметра → id спрайта или `custom:…` */
  iconMapping?: Record<string, string>;
  /**
   * Снимок настроек по типу визуализации: при смене `type` настройки уходящего типа
   * прячутся сюда, а настройки приходящего — восстанавливаются. Round-trip без потерь;
   * неприменимые к новому типу настройки (напр. colorByProperty для heatmap) не протекают.
   * Сам ключ не вкладывается рекурсивно (без `styleByType` внутри бакетов).
   */
  styleByType?: Record<string, Partial<LayerStyleConfig>>;
  /**
   * Виз-блоки слоя — источник правды для рендера (новая форма). Если поле есть,
   * legacy-поля (`type`, `paint`, `layout`, пер-виз ключи корня, `styleByType`)
   * игнорируются. Читать только через `resolveVisualizations`.
   */
  visualizations?: LayerVisualization[];
}

export interface Layer {
  id: string;
  projectId: string;
  name: string;
  type: LayerType;
  sourceConfig: LayerSourceConfig;
  styleConfig: LayerStyleConfig;
  filterConfig: FilterConfig | null;
  sortOrder: number;
  visible: boolean;
  /** Editor-only: prevents feature interaction on the map (default false) */
  locked?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLayerRequest {
  name: string;
  type: LayerType;
  sourceConfig?: LayerSourceConfig;
  styleConfig?: LayerStyleConfig;
}

export interface UpdateLayerRequest {
  name?: string;
  sourceConfig?: LayerSourceConfig;
  styleConfig?: LayerStyleConfig;
  filterConfig?: FilterConfig | null;
  sortOrder?: number;
  visible?: boolean;
  locked?: boolean;
}
